/**
 * Wait list: /admin/waitlist. Loop-ADMIN-only. Formerly named "God View":
 * that name now belongs to the accounts/impersonation screen
 * (screens/admin/GodView.tsx). A COACH session never sees the nav entry that
 * reaches this screen (components/shell/ProjectNav.tsx), and this screen
 * redirects away for anyone else - but the real gate is server-side
 * (cloud-api's middleware/requireAdmin.ts): hiding the entry is cosmetic,
 * not the enforcement.
 *
 * Not wired through lib/api.ts's mock/real seam - see real-api.ts's header
 * for why. This screen has no demo value: the nav entry that reaches it is
 * hidden entirely in mock mode.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { showToast } from '../../components/primitives/Toast';
import { Table, type TableColumn } from '../../components/primitives/Table';
import { CopyField } from '../../components/patterns/CopyField';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { Skeleton } from '../../components/patterns/Skeleton';

import { useAccount } from '../../lib/auth/use-app-data';
import { useAsync } from '../../lib/hooks/use-async';
import { apiFetch, ApiError } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';

import '../../styles/screens/admin.css';

type WaitlistStatus = 'pending' | 'invited' | 'enrolled' | 'declined';

interface InviteSummaryWire {
  id: string;
  expires_at: string | null;
  consumed_at: string | null;
  revoked_at: string | null;
  created_at: string | null;
}

interface WaitlistEntryWire {
  id: string;
  email: string;
  building: string | null;
  status: WaitlistStatus;
  invited_at: string | null;
  decided_at: string | null;
  account_id: string | null;
  active_invite: InviteSummaryWire | null;
  created_at: string | null;
  updated_at: string | null;
}

interface ListResponse {
  entries: WaitlistEntryWire[];
  truncated: boolean;
}

interface InviteResponse {
  invite_url: string;
  expires_at: string;
  email_sent: boolean;
  email_error?: string;
}

async function loadWaitlist(): Promise<ListResponse> {
  return apiFetch<ListResponse>('/api/v1/admin/waitlist');
}

function statusLabel(status: WaitlistStatus): string {
  switch (status) {
    case 'pending':
      return t('Waiting');
    case 'invited':
      return t('Invited');
    case 'enrolled':
      return t('Enrolled');
    case 'declined':
      return t('Declined');
  }
}

export default function Waitlist() {
  const account = useAccount();
  const { data, isLoading, error, refetch } = useAsync(loadWaitlist, []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [justInvited, setJustInvited] = useState<{ email: string; url: string; emailSent: boolean } | null>(null);

  useEffect(() => {
    if (account.isAdmin && data) {
      captureEvent('admin_waitlist_viewed', {
        total_entries: data.entries.length,
      });
    }
  }, [account.isAdmin, data]);

  if (!account.isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  async function act(entryId: string, action: 'invite' | 'decline' | 'revoke') {
    setBusyId(entryId);
    captureEvent('admin_waitlist_action_clicked', {
      action,
      entry_id: entryId,
    });
    try {
      if (action === 'invite') {
        const res = await apiFetch<InviteResponse>(`/api/v1/admin/waitlist/${entryId}/invite`, { method: 'POST' });
        const entry = data?.entries.find((e) => e.id === entryId);
        setJustInvited({ email: entry?.email ?? '', url: res.invite_url, emailSent: res.email_sent });
        if (res.email_sent) showToast(t('Invite sent.'));
      } else if (action === 'decline') {
        await apiFetch(`/api/v1/admin/waitlist/${entryId}/decline`, { method: 'POST' });
        showToast(t('Marked declined.'));
      } else {
        await apiFetch(`/api/v1/admin/waitlist/${entryId}/revoke`, { method: 'POST' });
        showToast(t('Invite revoked.'));
      }
      refetch();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : t('Something went wrong. Try again in a moment.'));
    } finally {
      setBusyId(null);
    }
  }

  const columns: TableColumn<WaitlistEntryWire>[] = [
    { key: 'email', header: t('Email'), render: (row) => row.email },
    { key: 'status', header: t('Status'), render: (row) => statusLabel(row.status) },
    { key: 'building', header: t('Building'), render: (row) => row.building ?? '' },
    {
      key: 'actions',
      header: t('Actions'),
      align: 'end',
      render: (row) => (
        <div className="admin__actions">
          {(row.status === 'pending' || row.status === 'declined' || row.status === 'invited') && (
            <Button size="sm" variant="outline" loading={busyId === row.id} onClick={() => act(row.id, 'invite')}>
              {row.status === 'invited' ? t('Resend invite') : t('Invite')}
            </Button>
          )}
          {row.status === 'invited' && (
            <Button size="sm" variant="ghost" loading={busyId === row.id} onClick={() => act(row.id, 'revoke')}>
              {t('Revoke')}
            </Button>
          )}
          {row.status !== 'declined' && row.status !== 'enrolled' && (
            <Button size="sm" variant="ghost" loading={busyId === row.id} onClick={() => act(row.id, 'decline')}>
              {t('Decline')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Wait list')}</h1>
        <p className="page__lede">{t('Hand-pick who gets access to SV Cloud from the waitlist.')}</p>
      </header>

      {justInvited && (
        <Notice tone={justInvited.emailSent ? 'done' : 'attention'} title={justInvited.emailSent ? t('Invite sent') : t('Invite created')}>
          {!justInvited.emailSent && (
            <p className="page__lede">{t('The invite email could not be sent. Share this link with {email} directly.', { email: justInvited.email })}</p>
          )}
          <CopyField label={t('Invite link')} value={justInvited.url} truncateAt={48} />
        </Notice>
      )}

      {isLoading && <Skeleton shape="row" count={4} label={t('Loading the waitlist')} />}

      {error && <Notice tone="attention">{t('Could not load the waitlist. Try again in a moment.')}</Notice>}

      {data && data.entries.length === 0 && (
        <EmptyState mark="box" sentence={t('Nobody is on the waitlist yet.')} />
      )}

      {data && data.entries.length > 0 && (
        <Table
          caption={t('Waitlist')}
          columns={columns}
          rows={data.entries}
          rowKey={(row) => row.id}
        />
      )}

      {data?.truncated && <p className="page__lede">{t('Showing the most recent entries only.')}</p>}
    </div>
  );
}
